<script setup lang="ts">
import { api } from '@/api';
import { TrackItDataSequenceInfoDto } from '@/api/generator';
import Button from '@/components/buttons/Button.vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import { baseChartOptions } from '@/components/TrendChart/utils';
import { I18nKey } from '@/utils/typings';
import * as Highcharts from 'highcharts';
import { Chart } from 'highcharts-vue';
import { useTranslation } from 'i18next-vue';
import { merge } from 'lodash';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  curve: TrackItDataSequenceInfoDto;
}>();

const modelValue = defineModel<boolean>({ required: true });

const { t } = useTranslation();

const loading = ref(false);
const points = ref<[number, number][]>([]);
const positionUnit = ref('');

// A curve is two rows: this sequence holds the readings (Y) and its positions child holds
// where each reading was taken (X). Both are fetched by id and paired by index.
async function load() {
  const doseId = props.curve.id;
  const positionId = props.curve.positions?.id;

  if (doseId == null || positionId == null) {
    points.value = [];
    return;
  }

  loading.value = true;
  try {
    const [dose, position] = await Promise.all([
      api.trackItImportApi.getDataSequenceValues({ dataSequenceId: doseId }),
      api.trackItImportApi.getDataSequenceValues({ dataSequenceId: positionId }),
    ]);

    // `handle` always resolves and wraps the payload — a failure arrives as
    // { success: false, result: undefined } rather than a rejection.
    const y = dose.result?.values ?? [];
    const x = position.result?.values ?? [];
    positionUnit.value = position.result?.unit ?? '';

    // The importer rejects files whose position and value counts differ, so these are equal in
    // practice — truncating to the shorter of the two just avoids undefined if that ever changes.
    points.value = x
      .slice(0, Math.min(x.length, y.length))
      .map((mm, index): [number, number] => [mm, y[index]]);
  } finally {
    loading.value = false;
  }
}

watch(
  modelValue,
  (open) => {
    if (open) load();
  },
  { immediate: true },
);

// The unit is optional in the Track-it XML format and is genuinely blank in some exports, so
// never render a dangling "Position in ".
function axisTitle(label: string, unit: string) {
  return unit ? `${label} in ${unit}` : label;
}

const doseUnit = computed(() => props.curve.unit ?? '');

const profileOptions = computed<Highcharts.Options>(() => ({
  chart: {
    type: 'line',
    height: 460,
    spacingTop: 33,
    zooming: { type: 'x' },
  },
  legend: { enabled: false },
  xAxis: {
    type: 'linear',
    title: { text: axisTitle(t('TrackItPosition' as I18nKey), positionUnit.value) },
    crosshair: true,
  },
  yAxis: {
    // A profile is read about the beam centre, so the axis is drawn at position 0. A depth curve
    // starts at the surface, so 0 is its minimum and the axis lands at the left edge anyway.
    crossing: 0,
    // Not redundant: the default is 0, which renders the axis line with stroke-width="0".
    lineWidth: 1,
    labels: {
      // The X axis already labels the origin; a second 0 beside it only crowds the centre.
      formatter() {
        return this.value === 0 ? '' : this.axis.defaultLabelFormatter.call(this);
      },
    },
    // The unit is optional in the Track-it format; an empty title renders nothing, whereas
    // `undefined` would fall back to the Highcharts default of "Values".
    title: { text: doseUnit.value || '', align: 'high', rotation: 0, y: -20, x: 10 },
  },
  tooltip: {
    headerFormat: `${t('TrackItPosition' as I18nKey)}: {point.x:.1f} ${positionUnit.value}<br/>`,
    pointFormat: `<b>{point.y:.3f} ${doseUnit.value}</b>`,
  },
  series: [
    {
      type: 'line',
      name: props.curve.name ?? '',
      data: points.value,
      // Markers are useful on a 27-point array profile and noise on a 200-point scan.
      marker: { enabled: points.value.length <= 60 },
    },
  ],
}));

const chartOptions = computed(() => merge(baseChartOptions(), profileOptions.value));
</script>

<template>
  <Dialog
    :modelValue="modelValue"
    size="x-large"
    @update:modelValue="(value) => (modelValue = value)"
  >
    <template #title>
      <h3>{{ curve.name }}</h3>
    </template>

    <template #text>
      <v-progress-linear v-if="loading" indeterminate />

      <Chart v-else-if="points.length" :options="chartOptions" />

      <p v-else>{{ $t('NoData') }}</p>
    </template>

    <template #actions>
      <Button variant="secondary" :label="$t('Close')" @click="modelValue = false" />
    </template>
  </Dialog>
</template>
